import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, filter, firstValueFrom, map, Observable, of, Subject, switchMap, take, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { BookProvider } from '../providers/book.provider';
import { UserCompletedActivities, UserProvider } from '../providers/user.provider';
import { Book } from '../models/book.model';
import { UserBook } from '../models/user-book.model';
import { ToastService, ToastType } from './toast.service';
import { TranslateService } from '@ngx-translate/core';
import { LoadingService } from './loading.service';
import { NavigationService } from './navigation.service';

export interface BookData {
  book: Book;
  userBook: UserBook;
  userCompletedActivities: UserCompletedActivities[];
}
/**
 * The purpose of this service is to keep track & data of which book is currently open
 * on the Book Details / Reader or Book Completion pages, so that you don't have to call up the api every time.
 */
@Injectable({ providedIn: 'root' })
export class BookService {
  constructor(
    private booksProvider: BookProvider,
    private userProvider: UserProvider,
    private navigationService: NavigationService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private loadingService: LoadingService,
  ) {}

  book$: BehaviorSubject<Book | null> = new BehaviorSubject<Book | null>(null);
  userBook$: Subject<UserBook | null> = new BehaviorSubject<UserBook | null>(null);
  userCompletedActivities$: Subject<UserCompletedActivities[] | null> = new BehaviorSubject<UserCompletedActivities[] | null>(null);

  // Combine book$ & userBook$ & avoid sending null values
  bookData$: Observable<BookData> = combineLatest([this.book$, this.userBook$, this.userCompletedActivities$]).pipe(
    map(([book, userBook, userCompletedActivities]) => {
      return { book, userBook, userCompletedActivities } as {
        book: Book;
        userBook: UserBook;
        userCompletedActivities: UserCompletedActivities[];
      };
    }),
    filter(({ book, userBook, userCompletedActivities }) => book !== null && userBook !== null && userCompletedActivities !== null),
  );

  bookComplete$ = new BehaviorSubject<boolean>(false);

  async refreshUserBookInfo$(uuid: string): Promise<void> {
    const userBook = await firstValueFrom(this.userProvider.getUserBookInfo(uuid));
    this.userBook$.next(userBook);
  }

  // We need to refresh the completed activities api everytime a user complete an activity
  async refreshUserCompletedActivities$(uuid: string): Promise<void> {
    const userCompletedActivities = await firstValueFrom(this.userProvider.getUserCompletedActivities(uuid));
    this.userCompletedActivities$.next(userCompletedActivities);
  }

  async getBook(uuid: string): Promise<void> {
    // Don't reload the same book if it's already loaded - we just need to get updated data of user books
    if (this.book$.value?.uuid === uuid) {
      // TODO check in progress ?
      void this.refreshUserBookInfo$(uuid);
      return Promise.resolve();
    } else {
      this.book$.next(null);
      this.userBook$.next(null);
    }

    combineLatest([this.booksProvider.getBook(uuid), this.userProvider.getUserBookInfo(uuid)])
      .pipe(
        take(1),
        switchMap(([book, userBook]) => {
          return this.userProvider.getUserCompletedActivities(uuid).pipe(
            catchError((error: HttpErrorResponse) => {
              this.bookError(error.status);

              return throwError(() => error);
            }),
            map(userCompletedActivities => {
              return {
                book,
                userBook,
                userCompletedActivities,
              };
            }),
          );
        }),
        catchError((error: HttpErrorResponse) => {
          this.bookError(error.status);

          if (error.status === 404) {
            return of();
          }

          return throwError(() => error);
        }),
      )
      .subscribe(async ({ book, userBook, userCompletedActivities }) => {
        this.book$.next(book);
        this.userBook$.next(userBook);
        this.userCompletedActivities$.next(userCompletedActivities);
      });
  }

  private bookError(errorStatus: number): void {
    if (errorStatus === 404) {
      void this.toastService.present({
        message: this.translateService.instant('PWA_homeLibrary_toast_bookNotFound'),
        type: ToastType.Negative,
        displayClose: true,
        cssClass: 'book-not-found-toast',
      });
      void this.loadingService.dismiss();
      void this.navigationService.navigate('/home');
    } else {
      void this.navigationService.navigate('/book-error');
    }
  }

  clear(): void {
    this.book$.next(null);
    this.userBook$.next(null);
  }
}
